import {
    formNameLabel,
    scheduleEmbedLabel,
    schedulePreviewEmbed,
} from "@/features/navigation/services/entityDisplayLabel";
import { supabaseClient } from "@/lib/supabase";
import { queryOptions, useQuery } from "@tanstack/react-query";

export interface AllValidationsEntryT {
    validation_id: string;
    entity_type: "form" | "schedule";
    entity_id: string;
    entity_name: string;
    is_valid: boolean | null;
    validated_at: string | null;
    created_at: string;
    reason: string | null;
    on_behalf_of_name: string | null;
    on_behalf_of_organisation: string | null;
    validated_by: string;
    validated_by_name: string | null;
    validated_by_email: string | null;
    /** When the form or schedule content last changed. */
    calculated_updated_at: string | null;
    /** Current validation for this entity, and the entity changed after it was resolved. */
    is_stale: boolean;
}

export interface AllValidationsPage {
    rows: AllValidationsEntryT[];
    total: number;
}

export type ValidationStatusFilterT = "all" | "pending" | "approved" | "rejected";

export const ALL_VALIDATIONS_PAGE_SIZE = 20;

const allValidationsSelect = `validation_id, is_valid, validated_at, created_at, reason, on_behalf_of_name, on_behalf_of_organisation, validated_by, profiles(first_name, last_name, email), form_validations(form_id, forms(form_name, slug, calculated_updated_at, validation_id)), schedule_validations(schedule_id, schedules(calculated_updated_at, validation_id, ${schedulePreviewEmbed}))`;

function mapRow(row: {
    validation_id: string;
    is_valid: boolean | null;
    validated_at: string | null;
    created_at: string;
    reason: string | null;
    on_behalf_of_name: string | null;
    on_behalf_of_organisation: string | null;
    validated_by: string;
    profiles: { first_name: string | null; last_name: string | null; email: string | null } | null;
    form_validations:
        | {
              form_id: string;
              forms: {
                  form_name: string | null;
                  slug: string | null;
                  calculated_updated_at: string | null;
                  validation_id: string | null;
              } | null;
          }[]
        | null;
    schedule_validations:
        | {
              schedule_id: string;
              schedules:
                  | (NonNullable<Parameters<typeof scheduleEmbedLabel>[0]> & {
                        calculated_updated_at: string | null;
                        validation_id: string | null;
                    })
                  | null;
          }[]
        | null;
}): AllValidationsEntryT[] {
    const isStale = (calculatedUpdatedAt: string | null | undefined, currentValidationId: string | null | undefined) =>
        Boolean(
            row.validated_at &&
            currentValidationId === row.validation_id &&
            calculatedUpdatedAt &&
            calculatedUpdatedAt > row.validated_at
        );
    const name = [row.profiles?.first_name, row.profiles?.last_name].filter(Boolean).join(" ").trim();
    const base = {
        validation_id: row.validation_id,
        is_valid: row.is_valid,
        validated_at: row.validated_at,
        created_at: row.created_at,
        reason: row.reason,
        on_behalf_of_name: row.on_behalf_of_name,
        on_behalf_of_organisation: row.on_behalf_of_organisation,
        validated_by: row.validated_by,
        validated_by_name: name || null,
        validated_by_email: row.profiles?.email ?? null,
    };

    const formLink = row.form_validations?.[0];
    if (formLink?.form_id) {
        return [
            {
                ...base,
                entity_type: "form",
                entity_id: formLink.form_id,
                entity_name: formNameLabel(formLink.forms),
                calculated_updated_at: formLink.forms?.calculated_updated_at ?? null,
                is_stale: isStale(formLink.forms?.calculated_updated_at, formLink.forms?.validation_id),
            },
        ];
    }
    const scheduleLink = row.schedule_validations?.[0];
    if (scheduleLink?.schedule_id) {
        return [
            {
                ...base,
                entity_type: "schedule",
                entity_id: scheduleLink.schedule_id,
                entity_name: scheduleEmbedLabel(scheduleLink.schedules),
                calculated_updated_at: scheduleLink.schedules?.calculated_updated_at ?? null,
                is_stale: isStale(scheduleLink.schedules?.calculated_updated_at, scheduleLink.schedules?.validation_id),
            },
        ];
    }
    return [];
}

/** Admin-facing: every validation across the org, page-based and optionally filtered by outcome, newest first - see validations-feature-concerns.md §4a. */
export async function selectAllValidations(
    page: number,
    status: ValidationStatusFilterT = "all",
    pageSize: number = ALL_VALIDATIONS_PAGE_SIZE
): Promise<AllValidationsPage> {
    const rangeFrom = page * pageSize;
    const rangeTo = rangeFrom + pageSize - 1;

    let query = supabaseClient.from("validations").select(allValidationsSelect, { count: "exact" });
    if (status === "pending") {
        query = query.is("is_valid", null);
    } else if (status === "approved") {
        query = query.eq("is_valid", true);
    } else if (status === "rejected") {
        query = query.eq("is_valid", false);
    }

    const { data, error, count } = await query.order("created_at", { ascending: false }).range(rangeFrom, rangeTo);
    if (error) throw error;

    return {
        rows: (data ?? []).flatMap(mapRow),
        total: count ?? 0,
    };
}

export const allValidationsQueryOptions = (
    page: number,
    status: ValidationStatusFilterT = "all",
    pageSize: number = ALL_VALIDATIONS_PAGE_SIZE
) =>
    queryOptions({
        queryKey: ["validations", "all", page, status, pageSize],
        queryFn: () => selectAllValidations(page, status, pageSize),
    });

export function useAllValidationsQuery(
    page: number,
    status: ValidationStatusFilterT = "all",
    pageSize: number = ALL_VALIDATIONS_PAGE_SIZE
) {
    return useQuery(allValidationsQueryOptions(page, status, pageSize));
}
